<template>
	<div id="device-info-table">
		<div class="vehicles-search-container">
      <v-row align="center"
      justify="center">
        <v-col cols="6">
          <v-autocomplete
            v-model="selectedOrg"
            :items="orgName"
            label="Organisations"
            item-text="name"
            outlined
            @change="fetchOrgNodes"
          />
        </v-col>

        <v-col cols="6" class="middle-align">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
          ></v-text-field>
        </v-col>
      </v-row>
    </div>

		<div class="download-container"> 
			<v-icon class="download-icon" @click="downloadFile">
				mdi-download
			</v-icon>
		</div>

		<TableRefresh 
			:state="loading"
			@click.native="reload"
		/>

		<v-data-table
			:headers="headers"
			:items="tableData"
			:loading="loading"
			:search="search"
			class="elevation-1 admin-table"
		>
			<template v-slot:progress>
				<v-progress-linear color="blue" height="2" indeterminate></v-progress-linear>
			</template>
	
		</v-data-table>
	</div>
</template>

<script>
	import { mapState, mapActions } from "vuex";

	import {
		GetOrgTree,
		getOrgDeviceInformation,
		GetOrgDevices
	} from "@/services/API.js";

	import { formatTZOffset } from "@/utils/index"

	import moment from "moment";
	import xlsx from 'xlsx';

	import TableRefresh from '@/components/general/TableRefresh.vue';


	import { Logger } from "aws-amplify";
	const logger = new Logger("device information");

	export default {
    name: 'DeviceInformation',
		components: {
			TableRefresh,
		},
		props: {
			update: {
				type: Number,
				default: 0
			}
		},
		data: () => ({
			search: '',
			headers: [
				{
          text: 'Vehicle',
          value: 'vehicle_name',
          align: 'start'
        },
				{
					text: 'Install Tag',
					value: 'install_tag',
					align: 'start'
				},
       	{
          text: 'Model',
          value: 'device_model',
          align: 'start'
        },
        {
          text: 'Firmware',
          value: 'firmware_version',
          align: 'start'
        },
        {
          text: 'IMEI',
          value: 'device_imei',
          align: 'start'
        },
        {
          text: 'ICCID',
          value: 'device_iccid',
          align: 'start'
        },
        {
          text: 'Cameras',
          value: 'cameras',
          align: 'start'
        },
        {
          text: 'I/O',
          value: 'io_pins',
          align: 'start'
        },
        {
          text: 'Storage (type, used, capacity)',
          value: 'storage',
          align: 'start'
        },
        {
          text: 'Timezone',
          value: 'tz',
          align: 'start'
        },
				{
          text: 'DST',
          value: 'dst_offset',
          align: 'start'
        }
      ],
			loading: false,
			date: moment().format("YYYY-MM-DD HH:mm:ss"),
			devices: [],
			deviceInfo: [],
      selectedOrg: '',
			tableData: []
		}),
		computed: {
			...mapState([
				'orgs'
			]),
      orgName() {
      	const orgs = this.orgs.map((org) => ({
					name: org.name
				}));

        return orgs;
      },
      orgId() {
        return this.retrunOrgID(this.selectedOrg);
      },
			deviceIds() {
				return this.devices.map(device => device.id);
			}
		},
		mounted() {
			window.EventBus.$on("wcToggleContacts", this.handleToggleContacts);
			window.EventBus.$on("showSelectContacts", this.handleContactSelection);
			window.EventBus.$on("refreshData", this.refreshData);
			
			this.getOrgs();
		},
		methods: {
			mapTableData() {
				const mappedInfo = this.deviceInfo.map(item => (
					{
						vehicle_name: this.returnVehicleName(item),
						install_tag: this.returnInstallTag(item),
						firmware_version: item.firmwareVersion,
						device_model: item.model,
						device_imei: this.returnDeviceComponentInfo(item.components, 'modem') ? this.returnDeviceComponentInfo(item.components, 'modem')['imei'] : '',
						device_iccid: this.returnDeviceComponentInfo(item.components, 'modem') ? this.returnDeviceComponentInfo(item.components, 'modem')['iccid'] : '',
						cameras: this.mapCameraInfo(this.returnDeviceComponentInfo(item.components, 'camera')),
						io_pins: this.mapIOInfo(this.returnDeviceComponentInfo(item.components, 'io')),
						storage: this.mapStorageInfo(this.returnDeviceComponentInfo(item.components, 'storage')),
						tz: formatTZOffset(item.time.tzo),
						dst_offset: formatTZOffset(item.time.dsto)
					}
				));
			
				this.tableData = mappedInfo;
			},	

			returnDeviceComponentInfo(data, type) {
				if(!data || !type ) return [];
				return data.find(item => item.type == type);
			},

			mapCameraInfo(cameras) {
				if (!cameras || !cameras.hasOwnProperty('channels')) return;
				let info = [];

				cameras.channels.forEach(channel => {
					let state = JSON.parse(channel.enabled.toLowerCase()); 
					let enabled = state ? '[ON]' : '[OFF]'

					let channelInfo = `${channel.name} ${enabled}`

					info.push(channelInfo);
				});

				return info;
			},

			mapIOInfo(io) {
				if (!io || !io.hasOwnProperty('pins')) return;
				let info = [];

				io.pins.forEach(pin => {
					info.push(pin.name);
				});

				return info;
			},

			mapStorageInfo(storage) {
				if (!storage || !storage.hasOwnProperty('devices')) return;
				let info = [];

				storage.devices.forEach(device => {
					let used = device.capacity.split('/')[0];
					let capacity = device.capacity.split('/')[1];
					const map = {
						'hdd': 'HDD',
						'sd': 'SD'
					};
					let storageInfo = `${map[device.medium]}, ${used} MB, ${capacity} MB \n`

					info.push(storageInfo);
				});

				return info;
			},

			...mapActions(["getOrgs"]),
      retrunOrgID(name) {
        let org = this.orgs.find(org => {
          return org.name == name;
        });

        return org ? org.id : '';
      },	

			batchDeviceRequest (id) {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async res => {
        try {
          // we need some arbitrary limit to prevent loading forever
          const maxPages = 10
          let currentPage = 0
          let more = true
          let fullList = []
          while(currentPage < maxPages && more) {
            let list = await GetOrgDevices(id, `page_num=${currentPage}&rows_per_page=1000&order_by=id`)
            if(list && list.Devices && Array.isArray(list.Devices) && list.Devices.length) {
              fullList = fullList.concat(list.Devices)

							if(list.Devices.length < 1000) {
								more = false
							}
            }
						else {
              more = false
            }
            currentPage++
          }
          res({Devices: fullList})
        } catch {
          res([])
        }  
      }) 
    },

			async getOrgDevices() {
				this.loading = true;
				const orgDevices = await getOrgDeviceInformation(this.orgId)

				if (orgDevices) {
					this.devices = orgDevices
				}
			},

			async fetchOrgNodes() {
        this.loading = true;
				this.devices = [];
				this.deviceInfo = [];
				this.tableData = [];
    		
				const orgTree = await GetOrgTree(this.orgId);
						
				if (orgTree) {
					this.nodes = orgTree;
					this.reload();
				}
			},
			

			async reload() {
				this.loading = true;
				this.devices = [];
				this.deviceInfo = [];
				this.tableData = [];
				let devices = await this.batchDeviceRequest(this.orgId);

				if(devices.Devices) {
					this.devices = devices.Devices;
					await this.batchGetDeviceInfomation(this.devices.map(device => device.id))
				}

						        
				this.mapTableData();
				this.loading = false;
			},

			// takes an array of device ids
			async batchGetDeviceInfomation(devices) {
				// Split devices into arrays of max 100
				const deviceInfoSets = [];
				for (let i = 0; i < devices.length; i += 100) {
					deviceInfoSets.push(devices.slice(i, i + 100))
				}
				
				// after we split our devices ids into sets, we can batch request the device info from the API 
				return new Promise(async resolve => {
					// if we had no devices we just return here
					if(!devices.length) {
						resolve(true)
					}

					//map our sets of device ids into promises
					let infoRequests = deviceInfoSets.map(deviceIdList => new Promise(async res => {
						getOrgDeviceInformation(this.orgId, deviceIdList).then(deviceInfo => {
							if (deviceInfo.Devices) {
								res(deviceInfo.Devices)
							} else {
								res([])
							}
						})		
					}))

					let deviceInfo = await Promise.all(infoRequests)
					this.deviceInfo = deviceInfo.flat()

					resolve(true)
				})
			},

			refreshData(data) {
				// Array of vehicle IDs that are updated
				let vehicleIds = data.IDs;

				// Find the updated vehicles in data table and map new vals to row item directly
				for (var i in this.vehicles) {
					for (var j in vehicleIds) {
						if (this.vehicles[i].id === vehicleIds[j]) {
							for (const [key, value] of Object.entries(data)) {
								// eslint-disable-next-line no-prototype-builtins
								if (this.vehicles[i].hasOwnProperty(key)) {
									this.vehicles[i][key] = value;
								}
							}
						}
					}
				}
			},

			returnInstallTag(data) {
				const installTag = this.devices.find(item => item.id == data.id);

				return installTag ? installTag.install_tag : '';
			},

			returnVehicleName(data) {
				const vehicle = this.devices.find(item => item.id == data.id);

				return vehicle ? vehicle.vehicle : '';
			},

			downloadFile () {
      if (this.tableData.length > 0) {
        // create our excel file
        const wb = xlsx.utils.book_new();
        
        // Filter unwanted fields from report
        let cleanedReportCollection = [];


        this.tableData.forEach(item => {
          let cleanedReportItem = {};
					
          this.headers.forEach(heading => {
            let key = heading.value;
						logger.info('key ', key)
            // eslint-disable-next-line no-prototype-builtins
            if (item.hasOwnProperty(key)) {   
							logger.info('prop ', item[key])          
              item[key] ? cleanedReportItem[key] = item[key].toString() : '';
            }
  
            if(!cleanedReportCollection.includes(cleanedReportItem)) {
              cleanedReportCollection.push(cleanedReportItem);
            }
          });
        });

        // create our worksheets
        const report = xlsx.utils.json_to_sheet(cleanedReportCollection);

        // apend them to the workbook
        xlsx.utils.book_append_sheet(wb, report, 'Device Information');

        const reportName = `${this.selectedOrg}_device_information_${moment().format("YYYY-MM-DD HH:mm:ss")}`;
        xlsx.writeFile(wb, reportName + ".xlsx");
      }
    }
		}
	};
</script>

<style lang="scss">
	.vehicles-search-container {
    width: 50%;
    padding-left: 1em;
  }
	.reports-action-launch {
    position: relative;
    cursor: pointer;
  }
	.report-action-options {
    display: none;
  }
	.active-options {
    display: block !important;  
  }
	.middle-align {
		margin-bottom: 40px;
	}
	.info-popup-container {
		border: solid #eeeeee 2px !important;
		background-color: white !important;
		-webkit-box-shadow: 1px 2px 6px 3px rgba(0,0,0,0.26); 
		box-shadow: 1px 2px 6px 3px rgba(0,0,0,0.26);
	}
	#device-info-table {
		.download-container {
			position: absolute;
    	right: 45px;
    	top: 10px;
		}
		.v-data-table { 
			overflow-x: auto;
		}
	}
</style>